/**
 * «Енергія»: the last complete day and the last 30 days (complete local
 * days), kWh, the table over the card's whole width — no chart by day (owner,
 * 21.10.2026). An object without PV has no PV rows. Under the table one line
 * on «Віддано в мережу» and «Розряд батареї» (D2).
 */
import type { EnergyTotals, SiteAnalysisResponse } from 'shared';

import { fmtDate, fmtNum } from '../../../components/format.js';
import { useLocale } from '../../../i18n/locale-context.js';
import type { MessageKey } from '../../../i18n/messages.js';

const ROWS: [MessageKey, keyof EnergyTotals, boolean][] = [
  ['site.energy.load', 'load_kwh', false],
  ['site.energy.pv', 'pv_kwh', true],
  ['site.energy.import', 'grid_import_kwh', false],
  ['site.energy.export', 'grid_export_kwh', false],
  ['site.energy.charge', 'charge_kwh', false],
  ['site.energy.discharge', 'discharge_kwh', false],
];

/** Energies under 0,1 kWh are rounding noise of the counters. */
const clean = (v: number) => (v < 0.1 ? 0 : v);

export function EnergyCard({
  analysis,
  hasPv,
}: {
  analysis: SiteAnalysisResponse | undefined;
  hasPv: boolean;
}) {
  const { t, tf, locale } = useLocale();
  const fmt = (v: number, d = 1) => fmtNum(locale, v, d);
  const last = analysis?.last_day ?? null;
  const month = analysis?.last_30_days ?? null;
  const rows = ROWS.filter(([, , pv]) => hasPv || !pv);

  return (
    <section className="card" aria-labelledby="energy-title">
      <div className="card-head">
        <h2 id="energy-title">{t('site.energy.title')}</h2>
        <span className="sub">{t('site.energy.sub')}</span>
      </div>
      {last || month ? (
        <>
          <table className="kv">
            <thead>
              <tr>
                <th>{t('site.energy.unit')}</th>
                <th>{last ? tf('site.energy.day', { date: fmtDate(last.date) }) : ''}</th>
                <th>{month ? tf('site.energy.month', { days: month.days }) : ''}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([key, field]) => (
                <tr key={field}>
                  <td>{t(key)}</td>
                  <td>{last ? fmt(clean(last.totals[field])) : ''}</td>
                  <td>{month ? fmt(clean(month.totals[field])) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="hint">{t('site.energy.explain')}</p>
          {month && month.restored_hours > 0 ? (
            <p className="hint">{tf('site.data.restored', { hours: month.restored_hours })}</p>
          ) : null}
        </>
      ) : (
        <p className="muted">{t('site.energy.none')}</p>
      )}
    </section>
  );
}
